import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import DuplicateWarning from './DuplicateWarning';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category labels for display
const CATEGORY_LABELS = {
  'division': 'División',
  'departamento': 'Departamento',
  'territorio': 'Territorio',
  'grupo': 'Grupo'
};

const ContactDialog = ({ open, onOpenChange, companyId, contact, onSuccess }) => {
  const [companies, setCompanies] = useState([]);
  const [typsaOrgUnits, setTypsaOrgUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    role: '',
    email: '',
    phone: '',
    company_id: companyId || '',
    owner: '',
    linkedin: '',
    typsa_org_unit_id: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchCompanies();
      fetchTypsaOrgUnits();
      fetchUsers();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    if (contact) {
      let firstName = contact.first_name || '';
      let lastName = contact.last_name || '';
      if (!firstName && contact.name) {
        const nameParts = contact.name.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      }
      
      setFormData({
        first_name: firstName,
        last_name: lastName,
        role: contact.role || '',
        email: contact.email || '',
        phone: contact.phone || '',
        company_id: contact.company_id || companyId || '',
        owner: contact.owner || '',
        linkedin: contact.linkedin || '',
        typsa_org_unit_id: contact.typsa_org_unit_id || ''
      });
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        role: '',
        email: '',
        phone: '',
        company_id: companyId || '',
        owner: '',
        linkedin: '',
        typsa_org_unit_id: ''
      });
    }
  }, [contact, companyId, open]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchTypsaOrgUnits = async () => {
    try {
      const response = await axios.get(`${API}/typsa-org-units`);
      setTypsaOrgUnits(response.data);
    } catch (error) {
      console.error('Error fetching TYPSA org units:', error);
    }
  };

  // Check if selected company is TYPSA
  const selectedCompany = companies.find(c => c.id === formData.company_id);
  const isTypsa = selectedCompany?.name?.toUpperCase().includes('TYPSA') || selectedCompany?.company_type === 'typsa';

  // Group TYPSA org units by category for display
  const getOrgUnitsByCategory = () => {
    const grouped = {};
    typsaOrgUnits.forEach(unit => {
      const cat = unit.category;
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(unit);
    });
    // Sort within each category
    Object.keys(grouped).forEach(cat => {
      grouped[cat].sort((a, b) => (a.value || '').localeCompare(b.value || '', 'es'));
    });
    return grouped;
  };

  const orgUnitsByCategory = getOrgUnitsByCategory();
  const selectedOrgUnit = typsaOrgUnits.find(u => u.id === formData.typsa_org_unit_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    
    // Require at least one contact method: email, phone, or linkedin
    const hasEmail = formData.email?.trim();
    const hasPhone = formData.phone?.trim();
    const hasLinkedin = formData.linkedin?.trim();
    
    if (!hasEmail && !hasPhone && !hasLinkedin) {
      toast.error('Debes proporcionar al menos un método de contacto: Email, Teléfono o LinkedIn');
      return;
    }

    // Clear TYPSA fields if not TYPSA company
    const dataToSend = { ...formData };
    if (!isTypsa) {
      dataToSend.typsa_org_unit_id = null;
    }

    setLoading(true);
    try {
      if (contact) {
        await axios.put(`${API}/contacts/${contact.id}`, dataToSend);
        toast.success('Contacto actualizado');
      } else {
        await axios.post(`${API}/contacts`, dataToSend);
        toast.success('Contacto creado');
      }
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving contact:', error);
      if (error.response?.status === 409) {
        toast.error('Ya existe un contacto con este email');
      } else {
        toast.error('Error al guardar el contacto');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle>{contact ? 'Editar Contacto' : 'Nuevo Contacto'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <DuplicateWarning
              entityType="contact"
              query={{
                name: `${formData.first_name || ''} ${formData.last_name || ''}`.trim(),
                email: formData.email,
              }}
              excludeId={contact?.id}
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Nombre *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  data-testid="contact-first-name-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">Apellidos</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  data-testid="contact-last-name-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="role">Cargo</Label>
                <Input
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  data-testid="contact-role-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="company">Empresa</Label>
                <Select
                  value={formData.company_id || "none"}
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    company_id: value === "none" ? "" : value,
                    // Clear TYPSA field when company changes
                    typsa_org_unit_id: ''
                  })}
                >
                  <SelectTrigger data-testid="contact-company-select">
                    <SelectValue placeholder="Seleccionar empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* TYPSA Specific Fields - Select from existing org units */}
            {isTypsa && (
              <div className="border rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/10 space-y-4">
                <div className="text-sm font-medium text-blue-700 dark:text-blue-400">
                  Organización TYPSA
                </div>
                <div>
                  <Label htmlFor="typsa_org_unit">Unidad Organizativa</Label>
                  <Select
                    value={formData.typsa_org_unit_id || "none"}
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      typsa_org_unit_id: value === "none" ? "" : value 
                    })}
                  >
                    <SelectTrigger data-testid="contact-typsa-org-unit-select">
                      <SelectValue placeholder="Seleccionar unidad organizativa" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {Object.entries(orgUnitsByCategory).map(([category, units]) => (
                        <React.Fragment key={category}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                            {CATEGORY_LABELS[category] || category}
                          </div>
                          {units.map((unit) => (
                            <SelectItem key={unit.id} value={unit.id}>
                              {unit.value}
                              {unit.owner && <span className="text-muted-foreground ml-2">({unit.owner})</span>}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                  {typsaOrgUnits.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      No hay unidades organizativas creadas. Créalas en la sección de Empresas.
                    </p>
                  )}
                </div>
                {selectedOrgUnit && (
                  <div className="text-sm bg-blue-100/50 dark:bg-blue-900/30 p-3 rounded">
                    <span className="font-medium">{CATEGORY_LABELS[selectedOrgUnit.category]}:</span>{' '}
                    {selectedOrgUnit.value}
                    {selectedOrgUnit.owner && (
                      <span className="text-muted-foreground ml-2">(Resp: {selectedOrgUnit.owner})</span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="owner">Responsable</Label>
                <Select
                  value={formData.owner || "none"}
                  onValueChange={(value) => setFormData({ ...formData, owner: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="contact-owner-select">
                    <SelectValue placeholder="Seleccionar responsable" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.email} value={user.name}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="linkedin">LinkedIn</Label>
                <Input
                  id="linkedin"
                  value={formData.linkedin}
                  onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                  placeholder="https://linkedin.com/in/..."
                  data-testid="contact-linkedin-input"
                />
              </div>
            </div>
            
            {/* Help text for required fields */}
            <p className="text-xs text-muted-foreground">
              * Nombre obligatorio + al menos un método de contacto (Email, Teléfono o LinkedIn)
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-contact-btn">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} data-testid="save-contact-btn">
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDialog;
